import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const BulkActions = ({ 
  selectedVerses, 
  onClearSelection, 
  onBulkAction,
  className = '' 
}) => {
  const [selectedAction, setSelectedAction] = useState('');
  const [bulkActionModalOpen, setBulkActionModalOpen] = useState(false);
  const [bulkPhase, setBulkPhase] = useState('');
  const [bulkReviewCount, setBulkReviewCount] = useState('');
  const [bulkStatus, setBulkStatus] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const actionOptions = [
    { value: '', label: 'Choose action...' },
    { value: 'practice', label: 'Start Practice Session' },
    { value: 'modifyPhase', label: 'Modify Phase' },
    { value: 'modifyReviews', label: 'Modify Review Count' },
    { value: 'changeStatus', label: 'Change Status' },
    { value: 'export', label: 'Export to File' },
    { value: 'import', label: 'Import from File' },
    { value: 'createSet', label: 'Create Study Set' },
    { value: 'addTags', label: 'Add Tags' },
    { value: 'delete', label: 'Remove from Library' }
  ];

  const phaseOptions = [
    { value: '', label: 'Select Phase...' },
    { value: 1, label: 'Phase 1 - Daily Reviews (30 days)' },
    { value: 2, label: 'Phase 2 - Gradual Increase (1-7 days)' },
    { value: 3, label: 'Phase 3 - Weekly Reviews (7 days)' },
    { value: 4, label: 'Phase 4 - Weekly to Monthly (7-30 days)' },
    { value: 5, label: 'Phase 5 - Monthly Reviews (30 days)' },
    { value: 6, label: 'Phase 6 - Monthly to Quarterly (30-90 days)' },
    { value: 7, label: 'Phase 7 - Quarterly Maintenance (90 days)' }
  ];

  const statusOptions = [
    { value: '', label: 'Select Status...' },
    { value: 'new', label: 'New' },
    { value: 'learning', label: 'Learning' },
    { value: 'reviewing', label: 'Reviewing' },
    { value: 'mastered', label: 'Mastered' }
  ];

  const handleExecuteAction = () => {
    if (!selectedAction || selectedVerses?.length === 0) return;

    // Handle actions that need confirmation or modal
    if (selectedAction === 'delete') {
      setDeleteConfirmOpen(true);
    } else if (['modifyPhase', 'modifyReviews', 'changeStatus']?.includes(selectedAction)) {
      setBulkActionModalOpen(true);
    } else {
      // Direct actions
      onBulkAction(selectedAction, selectedVerses);
      setSelectedAction('');
    }
  };

  const handleBulkModification = () => {
    if (!selectedAction || selectedVerses?.length === 0) return;

    const actionData = {
      action: selectedAction,
      verses: selectedVerses,
      ...(selectedAction === 'modifyPhase' && { phase: parseInt(bulkPhase) }),
      ...(selectedAction === 'modifyReviews' && { reviewCount: parseInt(bulkReviewCount) }),
      ...(selectedAction === 'changeStatus' && { status: bulkStatus })
    };

    onBulkAction('bulkModify', actionData);
    setBulkActionModalOpen(false);
    setBulkPhase('');
    setBulkReviewCount('');
    setBulkStatus('');
    setSelectedAction('');
  };

  const handleBulkDelete = () => {
    onBulkAction('bulkDelete', selectedVerses);
    setDeleteConfirmOpen(false);
    setSelectedAction('');
  };

  const resetModal = () => {
    setBulkActionModalOpen(false);
    setBulkPhase('');
    setBulkReviewCount('');
    setBulkStatus('');
    setSelectedAction('');
  };

  if (selectedVerses?.length === 0) {
    return null;
  }

  return (
    <>
      <div className={`bg-card border border-border rounded-lg shadow-warm p-4 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <Icon name="CheckSquare" size={18} className="text-primary" />
              <span className="text-sm font-medium text-foreground">
                {selectedVerses?.length} verse{selectedVerses?.length !== 1 ? 's' : ''} selected
              </span>
            </div>
            
            <div className="hidden sm:flex items-center space-x-2">
              <Select
                options={actionOptions}
                value={selectedAction}
                onChange={setSelectedAction}
                placeholder="Choose action..."
                className="min-w-48"
              />
              
              <Button
                variant="default"
                size="sm"
                onClick={handleExecuteAction}
                disabled={!selectedAction}
              >
                Execute
              </Button>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {/* Quick Actions */}
            <div className="hidden md:flex items-center space-x-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onBulkAction('practice', selectedVerses)}
                iconName="Brain"
                iconPosition="left"
              >
                Practice
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedAction('modifyPhase');
                  setBulkActionModalOpen(true);
                }}
                iconName="Settings"
                iconPosition="left"
              >
                Modify Phase
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => onBulkAction('export', selectedVerses)}
                iconName="Download"
                iconPosition="left"
              >
                Export
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeleteConfirmOpen(true)}
                iconName="Trash2"
                iconPosition="left"
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                Delete
              </Button>
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={onClearSelection}
              iconName="X"
              iconPosition="left"
            >
              Clear
            </Button>
          </div>
        </div>
        
        {/* Mobile Actions */}
        <div className="sm:hidden mt-3 pt-3 border-t border-border">
          <div className="flex items-center space-x-2">
            <Select
              options={actionOptions}
              value={selectedAction}
              onChange={setSelectedAction}
              placeholder="Choose action..."
              className="flex-1"
            />
            
            <Button
              variant="default"
              size="sm"
              onClick={handleExecuteAction}
              disabled={!selectedAction}
            >
              Execute
            </Button>
          </div>
        </div>
      </div>

      {/* Bulk Modification Modal */}
      {bulkActionModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-background border border-border rounded-lg shadow-warm-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                  <Icon name="Settings" size={20} className="text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-heading font-semibold text-foreground">
                    Bulk Modification
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {selectedVerses?.length} verses selected
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                {selectedAction === 'modifyPhase' && (
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      New Phase for All Selected Verses
                    </label>
                    <Select
                      options={phaseOptions}
                      value={bulkPhase}
                      onChange={setBulkPhase}
                      placeholder="Select phase..."
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      This will override the current phase for all selected verses
                    </p>
                  </div>
                )}

                {selectedAction === 'modifyReviews' && (
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      New Review Count for All Selected Verses
                    </label>
                    <Input
                      type="number"
                      min="0"
                      value={bulkReviewCount}
                      onChange={(e) => setBulkReviewCount(e?.target?.value)}
                      placeholder="Enter review count"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      This will set the review count for all selected verses
                    </p>
                  </div>
                )}

                {selectedAction === 'changeStatus' && (
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      New Status for All Selected Verses
                    </label>
                    <Select
                      options={statusOptions}
                      value={bulkStatus}
                      onChange={setBulkStatus}
                      placeholder="Select status..."
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      This will change the memorization status for all selected verses
                    </p>
                  </div>
                )}

                <div className="bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 rounded p-3">
                  <div className="flex items-start space-x-2">
                    <Icon name="AlertTriangle" size={16} className="text-yellow-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                        Bulk Modification Warning
                      </p>
                      <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
                        This action will modify {selectedVerses?.length} verses and cannot be undone. 
                        Make sure this is what you want to do.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 mt-6">
                <Button
                  variant="outline"
                  onClick={resetModal}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={handleBulkModification}
                  disabled={
                    (selectedAction === 'modifyPhase' && !bulkPhase) ||
                    (selectedAction === 'modifyReviews' && !bulkReviewCount) ||
                    (selectedAction === 'changeStatus' && !bulkStatus)
                  }
                >
                  Apply Changes
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {deleteConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-background border border-border rounded-lg shadow-warm-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex items-center justify-center w-10 h-10 bg-red-100 rounded-lg">
                  <Icon name="AlertTriangle" size={20} className="text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-heading font-semibold text-foreground">
                    Delete Multiple Verses
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    This action cannot be undone
                  </p>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <p className="text-sm text-foreground">
                  Are you sure you want to delete {selectedVerses?.length} verses from your library?
                </p>
                
                <div className="max-h-32 overflow-y-auto bg-muted/50 rounded p-2 border border-border">
                  {selectedVerses?.slice(0, 5)?.map((verse, index) => (
                    <div key={verse?.id || index} className="text-xs text-muted-foreground py-1">
                      • {verse?.reference}
                    </div>
                  ))}
                  {selectedVerses?.length > 5 && (
                    <div className="text-xs text-muted-foreground py-1 font-medium">
                      ... and {selectedVerses?.length - 5} more verses
                    </div>
                  )}
                </div>

                <p className="text-xs text-red-600">
                  All progress data, notes, and reflections for these verses will be permanently removed.
                </p>
              </div>

              <div className="flex items-center justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setDeleteConfirmOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={handleBulkDelete}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Delete {selectedVerses?.length} Verses
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BulkActions;